import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { authApiService } from "../../../../api/api";
import { IOrder, OrderStatus } from "../../../../types/order.type";
import { orderStatusSelector } from "../../../../utils/createSelector";
import { toDatetimeLocalValue } from "../../../../utils/formatDate";
import { getPaymentStatus } from "../../../../utils/getPaymentStatus";
import BlueButton from "../../../buttons/BlueButton/BlueButton";
import ModalBottomButtons from "../buttons/ModakBottomButtons/ModalBottomButtons";
import EditInput from "../inputs/EditInput/EditInput";
import SelectInput, { SelectOption } from "../inputs/SelectInput/SelectInput";
import ViewInput from "../inputs/ViewInput/ViewInput";
import styles from "./styles.module.scss";

interface Props {
  order: IOrder;
  onClose: () => void;
  onPressEdit: () => void;
  onApply: () => void;
}

const ViewOrderForm: React.FC<Props> = (props) => {
  const [status, setStatus] = useState<SelectOption>({
    value: props.order.status,
    label: getPaymentStatus(props.order.status),
  });
  const [ttn, setTttn] = useState<string>(props.order.ttn || "");
  const [invoice, setInvoice] = useState<string>(props.order.invoice ?? "");
  const [orderDateLocal, setOrderDateLocal] = useState<string>(
    toDatetimeLocalValue(props.order.createdAt)
  );
  const [clientFullName, setClientFullName] = useState<string>(
    props.order.client.fullName ?? ""
  );
  const [clientPhone, setClientPhone] = useState<string>(
    props.order.client.phone ?? ""
  );
  const [addressLine1, setAddressLine1] = useState<string>(
    props.order.adressLine1 ?? ""
  );
  const [addressLine2, setAddressLine2] = useState<string>(
    props.order.adressLine2 ?? ""
  );
  const [city, setCity] = useState<string>(props.order.city ?? "");
  const [stateVal, setStateVal] = useState<string>(props.order.state ?? "");
  const [postalCode, setPostalCode] = useState<string>(
    props.order.postalCode ?? ""
  );
  const [country, setCountry] = useState<string>(props.order.country ?? "");

  useEffect(() => {
    setInvoice(props.order.invoice ?? "");
    setOrderDateLocal(toDatetimeLocalValue(props.order.createdAt));
    setClientFullName(props.order.client.fullName ?? "");
    setClientPhone(props.order.client.phone ?? "");
    setAddressLine1(props.order.adressLine1 ?? "");
    setAddressLine2(props.order.adressLine2 ?? "");
    setCity(props.order.city ?? "");
    setStateVal(props.order.state ?? "");
    setPostalCode(props.order.postalCode ?? "");
    setCountry(props.order.country ?? "");
  }, [props.order]);

  const isViewStatus = false;
  // props.order.status === OrderStatus.PENDING ||
  // props.order.status === OrderStatus.PAID ||
  // props.order.status === OrderStatus.CANCELED;

  const onChangeStatus = async () => {
    try {
      if (status.value === OrderStatus.SHIPPED) {
        if (!ttn) {
          toast.error("TTN is required");
          return;
        }
        const response = await authApiService.shippedOrder(
          props.order.id.toString(),
          {
            ttn,
          }
        );
        if (response) {
          toast.success("Message for the client has been sent");
          props.onApply();
          props.order.status = status.value;
          props.order.ttn = ttn;
        }
      } else {
        const response = await authApiService.updateOrderStatus(
          props.order.id.toString(),
          { status: status.value }
        );

        if (response) {
          toast.success("Status changed successfully");
          props.onApply();
          props.order.status = status.value;
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const onSaveBasicFields = async () => {
    if (!orderDateLocal) {
      toast.error("Вкажіть дату замовлення");
      return;
    }
    try {
      const createdAt = new Date(orderDateLocal).toISOString();
      await authApiService.updateOrderStatus(props.order.id.toString(), {
        invoice: invoice.trim(),
        createdAt,
        clientFullName: clientFullName.trim(),
        clientPhone: clientPhone.trim(),
        adressLine1: addressLine1,
        adressLine2: addressLine2,
        city,
        state: stateVal,
        postalCode,
        country,
      });
      toast.success("Дані замовлення оновлено");
      props.onApply();
    } catch (err: any) {
      console.error(err);
      toast.error(
        err.response?.data?.message || err.message || "Не вдалося зберегти"
      );
    }
  };

  return (
    <div>
      <div className={styles.form}>
        <div className={styles.metaEditSection}>
          <EditInput
            title="Номер інвойса"
            value={invoice}
            setValue={setInvoice}
            isRequired={false}
          />
          <div className={styles.datetimeRow}>
            <label htmlFor="order-created-at" className={styles.datetimeLabel}>
              Дата замовлення
            </label>
            <input
              id="order-created-at"
              type="datetime-local"
              className={styles.datetimeInput}
              value={orderDateLocal}
              onChange={(e) => setOrderDateLocal(e.target.value)}
            />
          </div>
          <EditInput
            title="Ім'я клієнта"
            value={clientFullName}
            setValue={setClientFullName}
            isRequired={false}
          />
          <EditInput
            title="Телефон"
            value={clientPhone}
            setValue={setClientPhone}
            isRequired={false}
          />
          <EditInput
            title="Адреса (рядок 1)"
            value={addressLine1}
            setValue={setAddressLine1}
            isRequired={false}
          />
          <EditInput
            title="Адреса (рядок 2)"
            value={addressLine2}
            setValue={setAddressLine2}
            isRequired={false}
          />
          <div className={styles.infoContainer}>
            <EditInput
              title="Місто"
              value={city}
              setValue={setCity}
              isRequired={false}
            />
            <EditInput
              title="Область / регіон"
              value={stateVal}
              setValue={setStateVal}
              isRequired={false}
            />
          </div>
          <div className={styles.infoContainer}>
            <EditInput
              title="Індекс"
              value={postalCode}
              setValue={setPostalCode}
              isRequired={false}
            />
            <EditInput
              title="Країна"
              value={country}
              setValue={setCountry}
              isRequired={false}
            />
          </div>
          <BlueButton
            title="Зберегти зміни"
            onClick={onSaveBasicFields}
            isBigButton
          />
        </div>
        {isViewStatus ? (
          <>
            <ViewInput
              title="Status"
              value={getPaymentStatus(props.order.status)}
            />
          </>
        ) : (
          <div className={styles.selectContainer}>
            <SelectInput
              value={status}
              onChange={(value) => setStatus(value)}
              values={orderStatusSelector}
              title="Status"
            />
            {props.order.status !== status.value && (
              <>
                {status.value === OrderStatus.SHIPPED && (
                  <EditInput
                    title="TTN"
                    value={ttn}
                    setValue={(value) => setTttn(value)}
                    isRequired
                  />
                )}
                <BlueButton title="Change status" onClick={onChangeStatus} />
              </>
            )}
          </div>
        )}
        {props.order.ttn && <ViewInput title="TTN" value={props.order.ttn} />}
        <ViewInput
          title="Email"
          value={
            props.order.client.email ? props.order.client.email : "empty"
          }
        />
        <ViewInput title="Total amout" value={`${props.order.totalAmount} €`} />
        {props.order.items.map((item) => (
          <div key={item.id} className={styles.productContainer}>
            <ViewInput
              title="Product"
              value={`${item.product.name} / ${item.product.capacity}`}
            />
            <ViewInput title="Count" value={item.quantity.toString()} />
          </div>
        ))}
      </div>
      <ModalBottomButtons
        backTitle="Назад"
        onPressBack={props.onClose}
        applyTitle="Списання зі складу"
        onPressApply={
          props.order.status === OrderStatus.PAID ? props.onPressEdit : undefined
        }
      />
    </div>
  );
};

export default ViewOrderForm;
